// =============================================
// BackdateScheduleDialog — rozkłada daty publikacji partii artykułów na X dni wstecz.
//
// Flow:
//   1. User otwiera dialog (np. z panelu QueueDashboard "Kolejka publikacji")
//   2. Suwakiem wybiera X dni wstecz (1-90, default 14)
//   3. Wybiera tryb rozkładu: równomiernie / skupione w ostatnich dniach
//   4. Preview pokazuje 5 przykładowych dat z wygenerowanego rozkładu
//   5. Klik "Zastosuj" → applyBackdatedDates persistuje effective_publish_date w DB
//
// Daty trafiają potem do schema.org datePublished + WP post.date_gmt
// (przez publishQueueService + schemaGenerator).
// =============================================

import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar, Sparkles } from "lucide-react";
import {
  distributeBackdatedDates,
  applyBackdatedDates,
  type DistributionMode,
  type BackdateConfig,
} from "@/services/backdateScheduler";
import { useToast } from "@/hooks/use-toast";

interface BackdateScheduleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nodeIds: string[];
  onApplied?: () => void;
}

const DEFAULT_DAYS_BACK = 14;

function formatDatePL(d: Date): string {
  return d.toLocaleString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function BackdateScheduleDialog({
  open,
  onOpenChange,
  nodeIds,
  onApplied,
}: BackdateScheduleDialogProps) {
  const [daysBack, setDaysBack] = useState(DEFAULT_DAYS_BACK);
  const [distribution, setDistribution] = useState<DistributionMode>("uniform");
  const [workHoursOnly, setWorkHoursOnly] = useState(true);
  const [applying, setApplying] = useState(false);
  const { toast } = useToast();

  const config: BackdateConfig = useMemo(
    () => ({ daysBack, distribution, workHoursOnly }),
    [daysBack, distribution, workHoursOnly],
  );

  // Preview — pokazuje pierwsze 3 + ostatnie 2 daty z wygenerowanego rozkładu
  const previewDates = useMemo(() => {
    if (nodeIds.length === 0) return [];
    const distributed = distributeBackdatedDates(nodeIds, config);
    if (distributed.length <= 5) return distributed.map((d) => d.publishDate);
    return [
      distributed[0].publishDate,
      distributed[1].publishDate,
      distributed[Math.floor(distributed.length / 2)].publishDate,
      distributed[distributed.length - 2].publishDate,
      distributed[distributed.length - 1].publishDate,
    ];
  }, [nodeIds, config]);

  const handleApply = async () => {
    setApplying(true);
    try {
      const result = await applyBackdatedDates(nodeIds, config);
      toast({
        title: "Daty zaplanowane",
        description: `${result.updated} wpisów zaktualizowanych` +
          (result.skippedPublished > 0 ? `, ${result.skippedPublished} pominiętych (już opublikowane)` : ""),
      });
      onApplied?.();
      onOpenChange(false);
    } catch (e) {
      toast({
        title: "Błąd",
        description: e instanceof Error ? e.message : "Nieznany",
        variant: "destructive",
      });
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Zaplanuj daty publikacji wstecz
          </DialogTitle>
          <DialogDescription>
            Rozłoży {nodeIds.length} artykułów na wybrane okno czasowe.
            Daty trafią do schema.org datePublished + WordPress post.date_gmt.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Slider dni wstecz */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Zakres wstecz</Label>
              <span className="text-sm font-mono text-primary">
                {daysBack} {daysBack === 1 ? "dzień" : daysBack < 5 ? "dni" : "dni"}
              </span>
            </div>
            <Slider
              value={[daysBack]}
              min={1}
              max={90}
              step={1}
              onValueChange={(v) => setDaysBack(v[0])}
            />
            <div className="flex justify-between text-[10px] text-muted-foreground">
              <span>1 dzień</span>
              <span>14 dni</span>
              <span>30 dni</span>
              <span>90 dni</span>
            </div>
          </div>

          {/* Rozkład */}
          <div className="space-y-2">
            <Label className="text-sm">Rozkład</Label>
            <RadioGroup
              value={distribution}
              onValueChange={(v) => setDistribution(v as DistributionMode)}
              className="gap-2"
            >
              <div className="flex items-start gap-2 rounded-md border p-2.5 hover:bg-muted/40 cursor-pointer">
                <RadioGroupItem value="uniform" id="uniform" className="mt-0.5" />
                <label htmlFor="uniform" className="flex-1 cursor-pointer">
                  <div className="text-sm font-medium">Równomiernie</div>
                  <div className="text-xs text-muted-foreground">
                    Równe interwały w oknie. Najpewniejsze.
                  </div>
                </label>
              </div>
              <div className="flex items-start gap-2 rounded-md border p-2.5 hover:bg-muted/40 cursor-pointer">
                <RadioGroupItem value="recent_heavy" id="recent_heavy" className="mt-0.5" />
                <label htmlFor="recent_heavy" className="flex-1 cursor-pointer">
                  <div className="text-sm font-medium">Skupione w ostatnich dniach</div>
                  <div className="text-xs text-muted-foreground">
                    Więcej w końcówce okna — mniej w głębi historii.
                  </div>
                </label>
              </div>
            </RadioGroup>
          </div>

          {/* Godziny */}
          <div className="flex items-start gap-2 rounded-md border p-2.5">
            <Checkbox
              id="work-hours"
              checked={workHoursOnly}
              onCheckedChange={(v) => setWorkHoursOnly(v === true)}
              className="mt-0.5"
            />
            <label htmlFor="work-hours" className="flex-1 cursor-pointer text-sm">
              <div className="font-medium">Tylko godziny pracy (9-17)</div>
              <div className="text-xs text-muted-foreground">
                Naturalniej. Bez tego: 6-22 (cały dzień).
              </div>
            </label>
          </div>

          {/* Preview */}
          {previewDates.length > 0 && (
            <div className="rounded-md bg-muted/40 p-3 space-y-1.5">
              <div className="flex items-center gap-1.5 text-xs font-medium">
                <Sparkles className="h-3 w-3 text-primary" />
                Podgląd dat ({previewDates.length} z {nodeIds.length})
              </div>
              <div className="font-mono text-[11px] space-y-0.5">
                {previewDates.map((d, i) => (
                  <div key={i} className="text-muted-foreground">
                    {formatDatePL(d)}
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground italic pt-1">
                Najstarsza ↑ &nbsp;→&nbsp; Najnowsza ↓ (deterministyczne — regeneracja daje ten sam wynik)
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={applying}>
            Anuluj
          </Button>
          <Button onClick={handleApply} disabled={applying || nodeIds.length === 0}>
            {applying ? "Zastosowuję..." : `Zastosuj do ${nodeIds.length} wpisów`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
